import { useState } from "react";
import { Play } from "lucide-react";
import { Reveal, Eyebrow, CTAButton } from "./Shared";
import { IMAGES, VIDEO_URL } from "../config";

export default function VideoSection() {
  const [playing, setPlaying] = useState(false);

  return (
    <section data-testid="video-section" className="relative py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <Reveal>
          <Eyebrow>Your business is not separate from you</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl max-w-3xl">
            The person building it is part of the{" "}
            <span className="font-serif italic normal-case font-medium text-lime">blueprint.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="relative mt-10 sm:mt-14 aspect-video overflow-hidden border border-cream/10 group cursor-pointer bg-black"
            onClick={() => VIDEO_URL && setPlaying(true)}
          >
            {playing && VIDEO_URL ? (
              <video
                src={VIDEO_URL}
                controls
                autoPlay
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="video-player"
              />
            ) : (
              <>
                <img
                  src={IMAGES.dasha}
                  alt="Dasha Love — A minute with Dasha"
                  className="absolute inset-0 w-full h-full object-cover object-[15%_35%] grayscale contrast-125 transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-ink/45 transition-opacity duration-500 group-hover:opacity-30" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
                  <span className="relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24">
                    <span className="absolute inset-0 rounded-full border border-lime/60 animate-pulse-ring" />
                    <button
                      data-testid="video-play-button"
                      aria-label="Press play — A minute with Dasha"
                      className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-lime text-ink flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    >
                      <Play size={26} fill="currentColor" className="ml-1" />
                    </button>
                  </span>
                  <div className="text-center">
                    <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-cream/80">
                      Press play
                    </p>
                    <p className="mt-2 font-display font-extrabold uppercase tracking-tight text-xl sm:text-3xl">
                      A minute with <span className="text-lime">Dasha</span>
                    </p>
                  </div>
                </div>
                <span className="absolute bottom-4 right-4 font-mono text-[10px] tracking-[0.2em] bg-ink/70 backdrop-blur px-2.5 py-1 border border-cream/15">
                  01:00
                </span>
              </>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 sm:mt-14 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 space-y-4 text-sm sm:text-base leading-relaxed text-fog">
              <p className="text-cream font-medium">
                You don&rsquo;t create your next level by thinking like the version of you who
                built your current one.
              </p>
              <p>You can change your strategy. You can rewrite your offer. You can set a bigger income goal.</p>
              <p>
                But if your beliefs, identity, message, and decisions are still organized around
                your old reality, you may keep recreating the same limits in a different form.
              </p>
              <p className="text-cream">
                BLUEPRINT helps you examine the person, purpose, message, and offer underneath
                the plan.
              </p>
            </div>
            <div className="lg:col-span-5 flex lg:justify-end items-start">
              <CTAButton testId="video-cta-button">Get Blueprint — $111</CTAButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
